import { ChangeEvent, useCallback, useRef } from "react";
import clsx from "clsx";

import { Checkbox, CheckboxState } from "react-migration/components/DeprecatedCheckbox";
import { Switch } from "react-migration/components/Switch";
import { Icon } from "react-migration/components/Icon";
import { Button } from "react-migration/components/Button/Button";

import { useControlTracking } from "../../ControlTrackingContext";
import { LayerTypeControlPageProps } from "../../types";
import { router } from "src/js/router/react-router";
import { BundleId } from "src/js/stores/map/store";
import { updateMapSettings } from "src/js/stores/map/actions/updateMapSettings";
import ROUTES from "src/js/router/routes";

import { PlanningApplicationsFilters } from "./components/PlanningApplicationsFilters";
import { ClassificationToggles } from "./components/ClassificationToggles";
import { usePlanningApplicationsContext } from "./PlanningApplicationsProvider";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";

export function PlanningApplicationsControlPage({ disabled }: LayerTypeControlPageProps) {
  const { state, stateDispatch } = usePlanningApplicationsContext();
  const { debouncedTrackControlChange } = useControlTracking();
  const { showBoundaries, description } = state;
  const descriptionInputRef = useRef<HTMLInputElement>(null);

  const handleShowBoundariesChange = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      debouncedTrackControlChange({ filterName: "showBoundaries", filterState: ev.target.checked });
      stateDispatch({ type: "SET_SHOW_BOUNDARIES", value: ev.currentTarget.checked });
    },
    [stateDispatch, debouncedTrackControlChange]
  );

  const handleDescriptionChange = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      debouncedTrackControlChange({ filterName: "description", filterState: ev.target.value });
      stateDispatch({ type: "SET_DESCRIPTION", value: ev.target.value });
    },
    [stateDispatch, debouncedTrackControlChange]
  );

  const handleShowAppealedChange = useCallback(
    (checked: boolean) => {
      debouncedTrackControlChange({ filterName: "showAppealed", filterState: checked });
      stateDispatch({ type: "SET_SHOW_APPEALED", value: checked });
    },
    [stateDispatch, debouncedTrackControlChange]
  );

  const handleClearDescription = useCallback(() => {
    debouncedTrackControlChange({ filterName: "description", filterState: "" });
    stateDispatch({ type: "SET_DESCRIPTION", value: "" });
    if (descriptionInputRef.current) {
      descriptionInputRef.current.value = "";
    }
  }, [stateDispatch, debouncedTrackControlChange]);

  const handleSearchClick = useCallback(() => {
    updateMapSettings("activeSearch", BundleId.PLANNING);

    const mapState = JSON.parse(localStorage?.mapStateSettings || '{ "mapX": 53, "mapY": 0, "zoom": 10 }');
    /**
     * calling this synchronously seems to cause an error with deck.gl when navigating
     * from planning applications search results, to the map layers and then back to the planning applications search results
     * delaying this by a small amount seems to allow the removeFromMap call above to complete
     * before navigating and mounting a new google map instance
     */
    const stall = window.requestIdleCallback || ((cb) => setTimeout(cb, 100));
    stall(() => {
      router.push({
        name: ROUTES.LEGACY_SEARCH,
        params: {
          x: mapState.mapX,
          lat: mapState.mapX,
          y: mapState.mapY,
          lng: mapState.mapY,
          z: mapState.zoom,
          searchType: BundleId.PLANNING,
        },
      });
    });
  }, []);

  return (
    <div className="space-y-4 pb-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between pb-4 border-b border-neutral-200">
          <label htmlFor="planning-show-boundaries" className={clsx("font-semibold", { "opacity-50": disabled })}>
            Show available boundaries
          </label>
          <div className="flex items-center justify-end">
            <Switch
              disabled={disabled}
              id="planning-show-boundaries"
              label="Show available boundaries"
              onToggle={handleShowBoundariesChange}
              value={showBoundaries}
            />
          </div>
        </div>

        <div className="pt-2 pb-4 space-y-2 border-b border-neutral-200">
          <label htmlFor="planning-description" className={clsx("font-semibold", { "opacity-50": disabled })}>
            Text search
          </label>
          <div className="relative">
            <input
              ref={descriptionInputRef}
              id="planning-description"
              type="text"
              defaultValue={description}
              onChange={handleDescriptionChange}
              disabled={disabled || !hasFeature(Feature.planningFilters)}
              placeholder={
                hasFeature(Feature.planningFilters)
                  ? 'Search (use "quotes" for exact matches)'
                  : "Upgrade to pro to enable text search"
              }
              className={clsx(
                "rounded-lg border px-3 grow w-full",
                "bg-background-input border-border-action placeholder:text-text-base-tertiary",
                "h-9 placeholder:text-sm text-sm",
                "focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                {
                  "opacity-50 cursor-not-allowed": disabled || !hasFeature(Feature.planningFilters),
                  "pr-8": description,
                }
              )}
            />
            {description && (
              <button
                type="button"
                onClick={handleClearDescription}
                disabled={disabled}
                className={clsx(
                  "absolute right-2 top-1/2 transform -translate-y-1/2",
                  "w-4 h-4 flex items-center justify-center",
                  "text-neutral-400 hover:text-neutral-600",
                  "transition-colors duration-200",
                  { "opacity-50 cursor-not-allowed": disabled }
                )}
                aria-label="Clear description filter"
              >
                <Icon
                  icon="icon-lt-close-line"
                  size="sm"
                  extraClasses="w-3 h-3 flex items-center justify-center"
                  cursor="pointer"
                />
              </button>
            )}
          </div>
        </div>

        <div className="pt-2 pb-4 space-y-2 border-b border-neutral-200">
          <div className="space-y-2">
            <ClassificationToggles
              disabled={disabled}
              prefix="planning"
              state={state}
              stateDispatch={stateDispatch}
              debouncedTrackControlChange={debouncedTrackControlChange}
            />
          </div>
        </div>
      </div>

      <PlanningApplicationsFilters
        disabled={disabled}
        state={state}
        stateDispatch={stateDispatch}
        debouncedTrackControlChange={debouncedTrackControlChange}
      />

      <div>
        <h1 className="text-sm mb-[10px]">Appealed:</h1>
        <label
          className="flex! items-center justify-between text-sm font-medium select-none"
          data-testid="planning-applications-appealed-only"
        >
          Show appealed applications only
          <Checkbox
            disabled={disabled}
            value={state.showAppealed ? CheckboxState.CHECKED : CheckboxState.EMPTY}
            onChange={handleShowAppealedChange}
          />
        </label>
      </div>
      <Button onClick={handleSearchClick} variant="primary" size="sm" className="w-full" leadIcon="lt-search-line">
        Search Planning Applications
      </Button>
    </div>
  );
}
